import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import { fmtDate, fmtDateTime } from '../types'
import type { DeliveryNote } from '../types'
import { classifyOverdue } from '../../../lib/overdue'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'
import { NoteSourceLink, useNoteSources } from '../NoteSourceLink'

// Z50.X (Option 1 Replace): this page used to read warehouse_inventory rows with
// status IN ('outbound','dispatched') and download a matching release_notes row -- neither
// that status nor the release_notes table ever existed live (confirmed via PostgREST), so
// there was nothing here to carry forward. "Outbound" = delivery_notes with
// lifecycle_status='COMPLETED'. Read-only history: no dialogs, just View PDF and, for a
// note that was completed by mistake, Reopen (back to Planned) / Void.

const TYPE_LABEL: Record<DeliveryNote['note_type'], string> = {
  DELIVERY: 'Delivery', COLLECTION_COURIER: 'Collection (Courier)',
  COLLECTION_SELF: 'Collection (Self)', COLLECTION_SUPPLIER: 'Collection (Supplier)',
}
const TYPE_CLS: Record<DeliveryNote['note_type'], string> = {
  DELIVERY:            'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  COLLECTION_COURIER:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  COLLECTION_SELF:     'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  COLLECTION_SUPPLIER: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
}

type DateFilter = 'today' | 'week' | 'month' | 'all' | 'custom'
type FulfilmentFilter = 'all' | 'COLLECTION_ONLY' | 'COLLECTION_AND_DELIVERY'
type PodFilter = 'all' | 'has_pod' | 'missing_pod'

function daysBetween(fromISO: string, toISO: string): number {
  const from = new Date(fromISO.slice(0, 10) + 'T00:00:00')
  const to = new Date(toISO.slice(0, 10) + 'T00:00:00')
  return Math.round((to.getTime() - from.getTime()) / 86400000)
}

// Same in-transit thresholds as the Deliveries board (WARN_DAYS / ALERT_DAYS there).
const TRANSIT_WARN_DAYS = 3
const TRANSIT_ALERT_DAYS = 7
// When the goods left the warehouse: Collection Only notes finish at collection; a
// Collection+Delivery note has collected_at as soon as it is in transit (completed_at is only POD).
function leftAt(n: DeliveryNote): string {
  return (n.collected_at || n.completed_at || '') as string
}

function ukToday() { return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' }) }
function addDays(dateStr: string, n: number) {
  const d = new Date(dateStr + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10)
}
function ukWeekStart() {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }))
  const day = now.getDay(); const diff = day === 0 ? 6 : day - 1
  now.setDate(now.getDate() - diff)
  return now.toISOString().slice(0, 10)
}
function ukMonthStart() { return ukToday().slice(0, 7) + '-01' }

export default function OutboundPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [notes, setNotes] = useState<DeliveryNote[]>([])
  const noteSources = useNoteSources(notes)
  const [loading, setLoading] = useState(true)
  const [filterJob, setFilterJob] = useState('')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [customFrom, setCustomFrom] = useState(addDays(ukToday(), -30))
  const [customTo, setCustomTo] = useState(ukToday())
  const [fulfilmentFilter, setFulfilmentFilter] = useState<FulfilmentFilter>('all')
  const [podFilter, setPodFilter] = useState<PodFilter>('all')
  // Unlike the Devanning Notes list, history defaults to showing everything (VOID/SUPERSEDED
  // included -- this is the record of what actually happened), but staff can filter it out.
  const [hideVoided, setHideVoided] = useState<boolean>(() => {
    try { return localStorage.getItem('outbound.hideVoided') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('outbound.hideVoided', hideVoided ? '1' : '0') } catch { /* ignore */ }
  }, [hideVoided])

  const [jobProgress, setJobProgress] = useState<Record<string, { total_items: number; completed_items: number }>>({})

  const fetchNotes = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('delivery_notes')
      .select('*, devannings!inner(warehouse_id)')
      .eq('devannings.warehouse_id', id!)
      // Outbound = goods that have LEFT the warehouse: finished notes plus Collection+Delivery
      // notes confirmed collected and still in transit (lifecycle stays PLANNED until POD).
      .or('lifecycle_status.eq.COMPLETED,transit_status.eq.IN_TRANSIT')
    const rows = (data as DeliveryNote[] | null) || []
    rows.sort((a, b) => leftAt(b).localeCompare(leftAt(a)))
    setNotes(rows)
    const devIds = [...new Set(rows.map(r => r.devanning_id).filter(Boolean))] as string[]
    if (devIds.length) {
      const { data: c } = await supabase.from('v_job_completion_status').select('devanning_id, total_items, completed_items').in('devanning_id', devIds)
      setJobProgress(Object.fromEntries(((c as { devanning_id: string; total_items: number; completed_items: number }[] | null) || []).map(r => [r.devanning_id, r])))
    }
    setLoading(false)
  }, [id])

  useEffect(() => { fetchNotes() }, [fetchNotes])

  const [rangeFrom, rangeTo] = useMemo(() => {
    const today = ukToday()
    switch (dateFilter) {
      case 'today': return [today, today]
      case 'week':  return [ukWeekStart(), addDays(ukWeekStart(), 6)]
      case 'month': return [ukMonthStart(), addDays(ukMonthStart(), 31)]
      case 'all':   return ['0000-01-01', '9999-12-31']
      case 'custom': return [customFrom, customTo]
    }
  }, [dateFilter, customFrom, customTo])

  const visible = notes.filter(n => {
    if (hideVoided && n.status !== 'ACTIVE') return false
    if (filterJob && !(n.job_ref.toLowerCase().includes(filterJob.toLowerCase()) || String(n.serial_no).includes(filterJob))) return false
    if (fulfilmentFilter !== 'all' && n.fulfilment_type !== fulfilmentFilter) return false
    if (podFilter === 'has_pod' && !n.pod_received) return false
    if (podFilter === 'missing_pod' && n.pod_received) return false
    const day = (leftAt(n) || n.scheduled_date).slice(0, 10)
    return day >= rangeFrom && day <= rangeTo
  })

  // Z35-style pagination -- only the table body is sliced; nothing else on this page reads
  // `visible` so there's no risk of a stat card silently going per-page.
  const { page, setPage, pageSize, setPageSize } = usePagination('outbound-list')
  useEffect(() => { setPage(0) }, [filterJob, dateFilter, customFrom, customTo, hideVoided, fulfilmentFilter, podFilter]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = visible.length
  const paged = pageSize === 'all' ? visible : visible.slice(page * pageSize, page * pageSize + pageSize)

  async function reopenNote(note: DeliveryNote) {
    if (!window.confirm(`Reopen #${note.serial_no} back to Planned Outbound?`)) return
    // A Collection+Delivery note most likely got mistakenly confirmed DELIVERED -- reopening it
    // undoes just that (back to IN_TRANSIT, keeps collected_at) rather than the whole journey.
    // A Collection Only note has no separate delivery leg, so it goes straight back to PLANNED.
    const isCollectionAndDelivery = note.fulfilment_type === 'COLLECTION_AND_DELIVERY'
    if (isCollectionAndDelivery && note.lifecycle_status === 'PLANNED') {
      // Still in transit (not delivered): reopening undoes the collect -- back to the collection board.
      await supabase.from('delivery_notes').update({
        transit_status: 'PENDING', collected_at: null, collected_by: null,
      }).eq('id', note.id)
      await fetchNotes()
      return
    }
    await supabase.from('delivery_notes').update({
      lifecycle_status: 'PLANNED', completed_at: null, completed_by: null,
      ...(isCollectionAndDelivery
        ? { transit_status: 'IN_TRANSIT', delivered_at: null, delivered_by: null }
        : { collected_at: null, collected_by: null }),
    }).eq('id', note.id)
    await fetchNotes()
  }
  async function voidNote(note: DeliveryNote) {
    const reason = window.prompt(`Void #${note.serial_no}? Optional reason:`)
    if (reason === null) return
    await supabase.from('delivery_notes').update({
      status: 'VOID', voided_at: new Date().toISOString(), voided_by: user?.id || null, void_reason: reason || null,
    }).eq('id', note.id)
    await fetchNotes()
  }

  const { scrollProps: obScrollProps, contentRef: obTableRef, bar: obScrollBar } = useStickyHScroll([visible])

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Outbound</h2>
          <span className="text-sm text-gray-500">{visible.length} records</span>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Job Ref / Serial No</label>
            <input className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
              value={filterJob} onChange={e => setFilterJob(e.target.value)} placeholder="KI… / 9676" />
          </div>
          <div className="flex items-end gap-2">
            {([['today', 'Today'], ['week', 'This Week'], ['month', 'This Month'], ['all', 'All Time']] as [DateFilter, string][]).map(([k, label]) => (
              <button key={k} onClick={() => setDateFilter(k)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg ${dateFilter === k ? 'bg-blue-600 text-white' : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
            <button onClick={() => setDateFilter('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${dateFilter === 'custom' ? 'bg-blue-600 text-white' : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
              Custom
            </button>
            {dateFilter === 'custom' && (
              <>
                <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs dark:bg-gray-700 dark:text-white" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs dark:bg-gray-700 dark:text-white" value={customTo} onChange={e => setCustomTo(e.target.value)} />
              </>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={hideVoided} onChange={e => setHideVoided(e.target.checked)} />
            Hide voided/superseded
          </label>
          <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs dark:bg-gray-700 dark:text-white"
            value={fulfilmentFilter} onChange={e => setFulfilmentFilter(e.target.value as FulfilmentFilter)}>
            <option value="all">All Fulfilment Types</option>
            <option value="COLLECTION_ONLY">Collection Only</option>
            <option value="COLLECTION_AND_DELIVERY">Collection + Delivery</option>
          </select>
          <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-xs dark:bg-gray-700 dark:text-white"
            value={podFilter} onChange={e => setPodFilter(e.target.value as PodFilter)}>
            <option value="all">All POD</option>
            <option value="has_pod">Has POD</option>
            <option value="missing_pod">Missing POD</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...obScrollProps}>
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-gray-400">No outbound records in this range.</div>
            ) : (
              <table ref={obTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['JOB REF', 'SERIAL NO', 'TYPE', 'FULFILMENT', 'RELEASE TO', 'VEHICLE', 'PLTS', 'CTNS', 'SCHEDULED', 'LEFT WAREHOUSE', 'STATUS', 'DELIVERED AT', 'TRANSIT', 'POD', 'BY', 'ACTIONS'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {paged.map(note => (
                    <tr key={note.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2 font-mono text-xs text-blue-700 dark:text-blue-400 font-semibold whitespace-nowrap">{note.job_ref}
                        {note.devanning_id && jobProgress[note.devanning_id] && (
                          <span className="block text-[10px] font-normal text-gray-400 whitespace-nowrap">
                            {jobProgress[note.devanning_id].completed_items}/{jobProgress[note.devanning_id].total_items} rows left warehouse
                          </span>
                        )}
                        <NoteSourceLink note={note} source={noteSources[note.id]} warehouseId={id!} backLabel="Outbound" />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        #{note.serial_no}
                        {note.status === 'VOID' && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">VOID</span>}
                        {note.status === 'SUPERSEDED' && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">SUPERSEDED</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_CLS[note.note_type]}`}>{TYPE_LABEL[note.note_type]}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {note.fulfilment_type && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${note.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'}`}>
                            {note.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'Collection + Delivery' : 'Collection Only'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={note.release_to_name}>
                        {note.release_to_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-500 whitespace-nowrap">{note.vehicle_reg || '—'}</td>
                      <td className="px-3 py-2 text-xs font-semibold font-mono text-center">{note.pallets_qty}</td>
                      <td className="px-3 py-2 text-xs font-semibold font-mono text-center">{note.nop}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtDate(note.scheduled_date)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(leftAt(note) || null)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{(() => {
                        const inTransit = note.fulfilment_type === 'COLLECTION_AND_DELIVERY' && note.lifecycle_status !== 'COMPLETED'
                        if (!inTransit) return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Completed ✓</span>
                        const d = note.collected_at ? daysBetween(note.collected_at, new Date().toISOString()) : null
                        const lvl = classifyOverdue(d, { warnAtDays: TRANSIT_WARN_DAYS + 1, dangerAtDays: TRANSIT_ALERT_DAYS + 1 })
                        return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${lvl === 'danger' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'}`}
                          title="Left the warehouse; awaiting Confirm Delivered on the Deliveries board">🚚 In transit (awaiting POD){d != null ? ` · ${d}d` : ''}</span>
                      })()}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {note.fulfilment_type === 'COLLECTION_AND_DELIVERY' && note.delivered_at ? fmtDateTime(note.delivered_at) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {note.fulfilment_type === 'COLLECTION_AND_DELIVERY' && note.collected_at && note.delivered_at
                          ? `${daysBetween(note.collected_at, note.delivered_at)}d` : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {note.fulfilment_type === 'COLLECTION_AND_DELIVERY' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${note.pod_received ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {note.pod_received ? '✓ POD' : 'No POD'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{note.issued_by_name || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex gap-1.5 items-center">
                          {note.pdf_url && (
                            <button onClick={() => window.open(note.pdf_url!, '_blank')}
                              className="px-2 py-1 text-xs text-blue-600 hover:underline dark:text-blue-400">View PDF</button>
                          )}
                          <button onClick={() => reopenNote(note)} className="px-2 py-1 text-xs text-gray-500 hover:underline">Reopen</button>
                          <button onClick={() => voidNote(note)} className="px-2 py-1 text-xs text-red-500 hover:underline">Void</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {!loading && visible.length > 0 && (
            <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
          )}
        </div>
      </div>
      <StickyHScrollbar {...obScrollBar} />
    </WarehouseLayout>
  )
}
